import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { X, CalendarClock } from 'lucide-react';
import {
  agentPortalApi,
  AgentClientDetail,
  AgentCommittee,
  CommitteeDecision,
  formatDateTime,
  getCommitteeDecisionLabel,
} from '../../services/agentPortalService';
import { formatDate } from '../../services/agentBalancesService';
import { getBenefitTypeName } from '../../services/leadsService';
import { extractErrorMessage } from '../../utils/errorHandler';
import { ClientStageBadge } from './AgentStageBadge';
import StatusCellWithTooltip from '../clients/StatusCellWithTooltip';
import { EmptyState, ErrorState, LoadingState, Table, TableWrap, Td, Th, Tr, Muted } from './AgentPortalUI';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1100;
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 520px;
  max-width: 100vw;
  background: var(--surface-color);
  box-shadow: var(--shadow-heavy);
  z-index: 1101;
  display: flex;
  flex-direction: column;
  direction: rtl;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
`;

const DrawerTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 19px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-secondary);
  display: flex;

  &:hover {
    color: var(--text-primary);
  }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 16px;
  margin: 0;
  font-size: 14px;

  dt {
    color: var(--text-secondary);
  }

  dd {
    margin: 0;
    color: var(--text-primary);
    font-weight: 500;
  }
`;

const UpcomingBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--warning-color);
  border-right-width: 3px;
  border-radius: var(--border-radius-md);
  background: var(--warning-light);
  font-size: 14px;

  strong {
    display: block;
    color: var(--text-primary);
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
`;

const TableBorder = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  overflow: hidden;
`;

interface Props {
  clientId: number;
  onClose: () => void;
}

const ReferralDetailDrawer: React.FC<Props> = ({ clientId, onClose }) => {
  const [detail, setDetail] = useState<AgentClientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setError(null);

    agentPortalApi
      .getClientDetail(clientId, controller.signal)
      .then(setDetail)
      .catch(err => {
        if (axios.isCancel(err)) return;
        setError(extractErrorMessage(err, 'שגיאה בטעינת פרטי הלקוח'));
      });

    return () => controller.abort();
  }, [clientId, reloadKey]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Pending מהיום והלאה = ועדה צפויה (כמו בשרת); Pending שתאריכה עבר = ממתין להחלטה
  const getDecisionText = (committee: AgentCommittee) => {
    const startOfToday = new Date(new Date().toDateString());
    return committee.decisionStatus === CommitteeDecision.Pending && new Date(committee.committeeDate) >= startOfToday
      ? 'ועדה צפויה'
      : getCommitteeDecisionLabel(committee.decisionStatus);
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <Drawer role="dialog" aria-modal="true" aria-label="פרטי ההפניה">
        <DrawerHeader>
          <div>
            <DrawerTitle>{detail?.fullName ?? 'פרטי ההפניה'}</DrawerTitle>
            {detail && <ClientStageBadge stage={detail.stage} />}
          </div>
          <CloseButton type="button" onClick={onClose} aria-label="סגירה">
            <X size={20} />
          </CloseButton>
        </DrawerHeader>

        <DrawerBody>
          {error && <ErrorState text={error} onRetry={() => setReloadKey(k => k + 1)} />}
          {!error && !detail && <LoadingState />}

          {detail && (
            <>
              <InfoGrid>
                <dt>סטטוס</dt>
                <dd>
                  <StatusCellWithTooltip
                    align="start"
                    currentStatusName={detail.statusName}
                    currentStatusColor={detail.statusColor}
                    currentStages={detail.currentStages}
                  />
                </dd>
                <dt>סוג קצבה</dt>
                <dd>{detail.benefitType ? getBenefitTypeName(detail.benefitType) : <Muted>-</Muted>}</dd>
                <dt>תאריך חתימה</dt>
                <dd>{formatDate(detail.signedAt)}</dd>
              </InfoGrid>

              {detail.upcomingCommittee && (
                <UpcomingBox>
                  <CalendarClock size={22} color="var(--warning-color)" />
                  <div>
                    <strong>ועדה צפויה</strong>
                    {formatDateTime(detail.upcomingCommittee.committeeDate)}
                  </div>
                </UpcomingBox>
              )}

              <div>
                <SectionTitle>היסטוריית ועדות</SectionTitle>
                {detail.committees.length === 0 ? (
                  <TableBorder>
                    <EmptyState text="טרם נקבעה ועדה לתיק זה" />
                  </TableBorder>
                ) : (
                  <TableBorder>
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <Th>תאריך</Th>
                            <Th>הערכה</Th>
                            <Th>החלטה</Th>
                            <Th>אחוז נכות</Th>
                            <Th>זכאות עד</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.committees.map((c, index) => (
                            <Tr key={index}>
                              <Td>{formatDate(c.committeeDate)}</Td>
                              <Td>{c.evaluationNumber}</Td>
                              <Td>{getDecisionText(c)}</Td>
                              <Td>{c.disabilityPercentage != null ? `${c.disabilityPercentage}%` : <Muted>-</Muted>}</Td>
                              <Td>{c.approvalEndDate ? formatDate(c.approvalEndDate) : <Muted>-</Muted>}</Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  </TableBorder>
                )}
              </div>
            </>
          )}
        </DrawerBody>
      </Drawer>
    </>
  );
};

export default ReferralDetailDrawer;
